import {
  Button,
  ChevronDownIcon,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  FileDownloadIcon,
  PlusIcon,
  Stack,
  Tab,
  TextInputField,
  TrashIcon,
} from '@fukuri-admins/ui/common';
import { Alert } from '@fukuri-admins/ui/common/Alert';
import { buttonVariants } from '@fukuri-admins/ui/shared/buttonVariants';
import styles from './styles.module.scss';
import { UploadStatusTable } from './UploadStatusTable';
import { UsersTable } from './UsersTable';

const TAB_ITEMS = [
  { value: 'users', label: '利用者一覧' },
  { value: 'uploadStatus', label: '登録予約一覧' },
] as const satisfies React.ComponentProps<typeof Tab>['items'];

type SelectedTab = (typeof TAB_ITEMS)[number]['value'];

type Props = {
  /**
   * 事業所未設定の利用者数（新 Alert 用）
   *
   * 1 以上の場合のみ新 Alert を表示する。
   */
  unsetBranchUserCount: number;
  /** 予約中アラートを表示するか */
  showScheduledAlert: boolean;
  /** 予約中アラートの「再読み込みして確認」押下時のコールバック */
  onReloadForScheduledAlert: React.ComponentProps<typeof Alert>['onAction'];
  /** 初期表示するタブ */
  selectedTab: SelectedTab;
  /** タブ切り替え時のコールバック */
  onTabChange: (value: SelectedTab) => void;
  /** 一括削除押下時のコールバック */
  onOpenBulkDeletionModal: () => void;
  /** 利用者データをダウンロード押下時のコールバック */
  onOutputCsv: () => void;
  /** アクティベーションコードをダウンロード押下時のコールバック */
  onOutputActivationCodeCsv: () => void;
  /** 個別追加押下時のコールバック */
  onClickAddUser: () => void;
  /** CSV一括追加・更新押下時のコールバック */
  onOpenBulkCreationModal: () => void;
  /** 名前検索フォームの値 */
  nameValue: string;
  /** メールアドレス検索フォームの値 */
  emailValue: string;
  /** 名前検索フォーム変更時のコールバック */
  onNameChange: (value: string) => void;
  /** メールアドレス検索フォーム変更時のコールバック */
  onEmailChange: (value: string) => void;
  /** 検索ボタン押下時のコールバック */
  onSearch: () => void;
  /** 条件をリセットボタン押下時のコールバック */
  onReset: () => void;
  /** テーブルに表示する利用者行データ */
  rows: React.ComponentProps<typeof UsersTable>['rows'];
  /**
   * ユーザーテーブルのソートキー押下時のコールバック
   *
   * @param key ソート対象のキー（'no' | 'email' | 'name' | 'branchName' | 'branchCode' | 'createdAt'）
   */
  onSortUsers: React.ComponentProps<typeof UsersTable>['onSort'];
  /**
   * 内部向け列（ログイン・アクティベーションコード・電話番号）を表示するか
   *
   * PartnerFeatureFlag の activation_code が有効かどうかの値を渡す
   */
  showInternalColumns: boolean;
  /**
   * 行の「編集」押下時のコールバック
   *
   * @param userId 操作対象の利用者 ID
   */
  onEditUser: (userId: number) => void;
  /**
   * 行の「削除」押下時のコールバック
   *
   * @param userId 操作対象の利用者 ID
   */
  onDeleteUser: (userId: number) => void;
  /**
   * 行クリック時のコールバック（利用者詳細画面への遷移）
   *
   * @param userId 操作対象の利用者 ID
   */
  onDetailClick: (userId: number) => void;
  /**
   * アクティベーションコードのコピーボタン押下時のコールバック
   *
   * @param code コピーするアクティベーションコード
   */
  onCopyActivationCode: (code: string) => void;
  /** 利用者テーブルのページャー props */
  pagerProps: React.ComponentProps<typeof UsersTable>['pagerProps'];
  /** テーブルに表示するアップロード状況行データ */
  uploadStatusRows: React.ComponentProps<typeof UploadStatusTable>['rows'];
  /**
   * アップロード状況テーブルのソートキー押下時のコールバック
   *
   * @param key ソート対象のキー（'status' | 'receptNo' | 'scheduledAt' | 'registeredCount'）
   */
  onSortUploadStatus: React.ComponentProps<typeof UploadStatusTable>['onSort'];
  /** アップロード状況テーブルのページャー props */
  uploadStatusPagerProps: React.ComponentProps<typeof UploadStatusTable>['pagerProps'];
  /**
   * アップロード状況テーブル行の「予約を取り消す」ボタン押下時のコールバック
   *
   * @param id 操作対象の一括登録リクエスト ID
   */
  onCancelReservation: (id: number) => void;
  /** 「実行日時」ヘルプアイコンのツールチップ表示ハンドラー */
  showTooltip: React.ComponentProps<typeof UploadStatusTable>['showTooltip'];
  /** ツールチップを非表示にするハンドラー */
  hideTooltip: React.ComponentProps<typeof UploadStatusTable>['hideTooltip'];
  /** ツールチップの id */
  tooltipId: React.ComponentProps<typeof UploadStatusTable>['tooltipId'];
  /**
   * CSVダウンロード（アクティベーションコードを含む）を表示するか
   *
   * PartnerFeatureFlag の activation_code が有効かどうかの値を渡す
   */
  showDownloadActivationCode?: boolean;
};

/**
 * 利用者一覧 Presentation
 */
export const UserListPresentation: React.FC<Props> = ({
  unsetBranchUserCount,
  showScheduledAlert,
  onReloadForScheduledAlert,
  selectedTab,
  onTabChange,
  onOpenBulkDeletionModal,
  onOutputCsv,
  onOutputActivationCodeCsv,
  onClickAddUser,
  onOpenBulkCreationModal,
  nameValue,
  emailValue,
  onNameChange,
  onEmailChange,
  onSearch,
  onReset,
  rows,
  onSortUsers,
  showInternalColumns,
  onEditUser,
  onDeleteUser,
  onDetailClick,
  onCopyActivationCode,
  pagerProps,
  uploadStatusRows,
  onSortUploadStatus,
  uploadStatusPagerProps,
  onCancelReservation,
  showTooltip,
  hideTooltip,
  tooltipId,
  showDownloadActivationCode,
}) => {
  return (
    <Stack gap={16}>
      {unsetBranchUserCount >= 1 && (
        <Alert
          title={`${unsetBranchUserCount}名の利用者に事業所を紐づけしてください`}
          description='ストレスチェックの報告書は事業所単位での作成が必要です。すべての利用者に事業所を紐づけしてください。'
          status='error'
        />
      )}
      {showScheduledAlert && (
        <Alert
          title='利用者の一括登録を予約しました'
          description='実行日時に登録を開始します。「登録予約一覧」タブよりステータスを確認できます。'
          status='info'
          action='再読み込みして確認'
          onAction={onReloadForScheduledAlert}
        />
      )}
      <Stack direction='row' alignItems='center' gap={16}>
        <div className={styles.tab}>
          <Tab items={TAB_ITEMS} defaultSelectedValue={selectedTab} onClick={onTabChange} />
        </div>
        <div className={styles.actions}>
          <DropdownMenu>
            <DropdownMenuTrigger>
              <button type='button' className={buttonVariants({ variant: 'primary' })}>
                新規追加・更新
                <ChevronDownIcon size='xs' />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem onSelect={onClickAddUser} className={styles.dropdownMenuItem}>
                <PlusIcon size='xs' />
                個別追加
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={onOpenBulkCreationModal}
                className={styles.dropdownMenuItem}
              >
                <PlusIcon size='xs' />
                CSV一括追加・更新
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </Stack>
      {selectedTab === 'users' && (
        <Stack direction='row' alignItems='flex-end' gap={8}>
          <div className={styles.searchField}>
            <TextInputField
              fieldWrapperProps={{
                htmlFor: 'user-list-name-input',
                labelText: '名前',
              }}
              textInput={{
                placeholder: '名前を入力',
                value: nameValue,
                onChange: e => onNameChange(e.target.value),
              }}
            />
          </div>
          <div className={styles.searchField}>
            <TextInputField
              fieldWrapperProps={{
                htmlFor: 'user-list-email-input',
                labelText: 'メールアドレス',
              }}
              textInput={{
                placeholder: 'メールアドレスを入力',
                value: emailValue,
                onChange: e => onEmailChange(e.target.value),
              }}
            />
          </div>
          <Button variant='secondary' type='button' onClick={onSearch}>
            検索
          </Button>
          <Button variant='ghost' type='button' onClick={onReset}>
            条件をリセット
          </Button>
          <div className={styles.searchActions}>
            <Button
              variant='ghostDestructive'
              icon={<TrashIcon size='xs' />}
              iconPosition='left'
              onClick={onOpenBulkDeletionModal}
            >
              一括削除
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger>
                <button type='button' className={buttonVariants({ variant: 'outline' })}>
                  <FileDownloadIcon size='xs' />
                  CSVダウンロード
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem onSelect={onOutputCsv} className={styles.dropdownMenuItem}>
                  <PlusIcon size='xs' />
                  利用者データをダウンロード
                </DropdownMenuItem>
                {showDownloadActivationCode && (
                  <DropdownMenuItem
                    onSelect={onOutputActivationCodeCsv}
                    className={styles.dropdownMenuItem}
                  >
                    <PlusIcon size='xs' />
                    アクティベーションコードをダウンロード
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </Stack>
      )}
      {selectedTab === 'users' && (
        <UsersTable
          rows={rows}
          onSort={onSortUsers}
          showInternalColumns={showInternalColumns}
          onEditUser={onEditUser}
          onDeleteUser={onDeleteUser}
          onDetailClick={onDetailClick}
          onCopyActivationCode={onCopyActivationCode}
          pagerProps={pagerProps}
        />
      )}
      {selectedTab === 'uploadStatus' && (
        <UploadStatusTable
          rows={uploadStatusRows}
          onSort={onSortUploadStatus}
          pagerProps={uploadStatusPagerProps}
          onCancelReservation={onCancelReservation}
          showTooltip={showTooltip}
          hideTooltip={hideTooltip}
          tooltipId={tooltipId}
        />
      )}
    </Stack>
  );
};
